import { Component, computed, inject, input, signal, ViewChild, AfterViewInit, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, ValidatorFn, AbstractControl } from '@angular/forms';
import { DynamicForm } from '@/ui/forms/dynamic-form/dynamic-form';
import { FormSchema } from '@/ui/forms/types';
import { DialogInjectableComponent } from '@/ui/dialog/types';
import { ToastService } from '@/ui/toast.service';
import { UsuariosStore } from '../store/usuarios.store';
import { CambiarPasswordFormModel } from '../models';
import { AuthStore } from '@/core/auth/store/auth.store';
import { PERMISSIONS } from '@/core/auth/permissions.generated';

// --- Section: Component ---
@Component({
  selector: 'app-cambiar-password-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DynamicForm],
  template: `
    <app-dynamic-form
      [schema]="schema"
      [initial]="{}"
      (formChange)="onFormChange($event)"
      (formValid)="onFormValid($event)" />
  `,
})
export class CambiarPasswordForm implements DialogInjectableComponent, AfterViewInit {
  private store = inject(UsuariosStore);
  private toast = inject(ToastService);
  private destroyRef = inject(DestroyRef);
  private auth = inject(AuthStore);

  readonly row = input.required<{ id: number; username: string }>();
  private readonly isOwnPassword = computed(() => this.auth.user.value()?.id === this.row().id);
  private readonly canResetOther = computed(() => this.auth.user.value()?.id != null
    && !this.isOwnPassword()
    && this.auth.hasPerm(PERMISSIONS.USUARIOS_UPDATE)
    && this.auth.hasPerm(PERMISSIONS.USUARIOS_PASSWORD_RESET));

  @ViewChild(DynamicForm) private dynamicForm!: DynamicForm<CambiarPasswordFormModel>;

  // --- Section: Form Schema ---
  protected readonly schema: FormSchema<CambiarPasswordFormModel> = [
    { key: 'oldPassword', labelKey: 'Contraseña Actual', type: 'password', required: true, maxLength: 100, hidden: () => this.canResetOther() },
    { key: 'newPassword', labelKey: 'Nueva Contraseña', type: 'password', required: true, maxLength: 100 },
    { key: 'confirmPassword', labelKey: 'Confirmar Nueva Contraseña', type: 'password', required: true, maxLength: 100 },
  ];

  // --- Section: State ---
  protected formValue = signal<Partial<CambiarPasswordFormModel>>({});
  protected formValid = signal(false);

  // --- Section: Handlers ---
  onFormChange(value: Partial<CambiarPasswordFormModel>) {
    this.formValue.set(value);
  }

  onFormValid(isValid: boolean) {
    this.formValid.set(isValid);
  }

  canSave(): boolean {
    return this.validatePasswords().valid;
  }

  async onSave(): Promise<boolean> {
    const result = this.validatePasswords();
    if (!result.valid) {
      this.toast.error(result.message!);
      return false;
    }

    const v = this.formValue();
    try {
      await this.store.changePassword(this.row().id, {
        newPassword: v.newPassword!.trim(),
        ...(this.isOwnPassword() ? { oldPassword: v.oldPassword!.trim() } : {}),
      });
      this.toast.success(`Contraseña de "${this.row().username}" actualizada correctamente.`);
      return true;
    } catch {
      this.toast.error('No se pudo cambiar la contraseña.');
      return false;
    }
  }

  // --- Section: Lifecycle ---
  ngAfterViewInit() {
    const form = this.dynamicForm.form;
    const confirmCtrl = form.get('confirmPassword')!;
    const newPwCtrl = form.get('newPassword')!;

    const mismatchValidator: ValidatorFn = (control: AbstractControl) => {
      const newPw = control.parent?.get('newPassword')?.value as string | undefined;
      const confirmPw = control.value as string | undefined;
      if (newPw && confirmPw && newPw !== confirmPw) {
        return { mismatch: true };
      }
      return null;
    };

    confirmCtrl.addValidators(mismatchValidator);
    confirmCtrl.updateValueAndValidity();

    const sub = newPwCtrl.valueChanges.subscribe(() => {
      confirmCtrl.updateValueAndValidity();
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  // --- Section: Private ---
  private validatePasswords(): { valid: boolean; message?: string } {
    const v = this.formValue();
    if (!this.isOwnPassword() && !this.canResetOther()) {
      return { valid: false, message: 'No tiene permisos para restablecer la contraseña de otro usuario.' };
    }
    if (this.isOwnPassword() && !v.oldPassword?.trim()) {
      return { valid: false, message: 'Ingrese la contraseña actual.' };
    }
    const pw = v.newPassword?.trim() ?? '';
    const confirm = v.confirmPassword?.trim() ?? '';
    if (pw.length < 6) return { valid: false, message: 'La contraseña debe tener al menos 6 caracteres.' };
    if (pw !== confirm) return { valid: false, message: 'Las contraseñas no coinciden.' };
    return { valid: true };
  }
}
